import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import Card from '../components/Card';
import Modal from '../components/Modal';
import TextInput from '../components/TextInput';
import ErrorMessage from '../components/ErrorMessage';
import CategoryManager from '../components/CategoryManager';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../utils/logger';

interface FamilyGroup {
  _id: string;
  name: string;
  description: string;
  admin: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  members: Array<{
    user: {
      _id: string;
      firstName: string;
      lastName: string;
    };
    role: string;
  }>;
}

interface Invitation {
  _id: string;
  name: string;
  description: string;
  admin: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  invitation: {
    token: string;
    role: string;
    expiresAt: string;
  };
}

const Groups: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [familyGroups, setFamilyGroups] = useState<FamilyGroup[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<Invitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<FamilyGroup | null>(null);
  const [groupToEdit, setGroupToEdit] = useState<FamilyGroup | null>(null);
  const [editedGroup, setEditedGroup] = useState({
    name: '',
    description: ''
  });
  const [newGroup, setNewGroup] = useState({
    name: '',
    description: ''
  });
  const [newInvite, setNewInvite] = useState({
    email: '',
    role: 'viewer' as 'editor' | 'viewer'
  });
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [acceptingInvite, setAcceptingInvite] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const fetchGroups = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/family-groups');
      setFamilyGroups(response.data);
      setError(null);
    } catch (error) {
      logger.error('Error fetching groups:', error);
      setError('Failed to load family groups. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchInvitations = async () => {
    try {
      const response = await api.get('/family-groups/invitations');
      setPendingInvitations(response.data);
    } catch (error) {
      logger.error('Error fetching invitations:', error);
      // Don't set error state here to avoid blocking the main UI
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await fetchGroups();
      await fetchInvitations();
    };
    fetchData();
  }, []);

  const handleCreateGroup = async () => {
    if (!newGroup.name.trim()) {
      setCreateError('Name is required');
      return;
    }

    try {
      setIsCreating(true);
      setCreateError(null);
      const response = await api.post('/family-groups', newGroup);
      if (response.status === 200 || response.status === 201) {
        setNewGroup({ name: '', description: '' });
        setIsCreateModalOpen(false);
        fetchGroups();
      }
    } catch (error) {
      logger.error('Error creating group:', error);
      setCreateError('Failed to create group. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleInviteMember = async () => {
    try {
      await api.post(`/family-groups/${selectedGroupId}/invite`, newInvite);
      setNewInvite({ email: '', role: 'viewer' });
      setIsInviteModalOpen(false);
      fetchGroups();
    } catch (error) {
      logger.error('Error inviting member:', error);
    }
  };

  const handleAcceptInvitation = async (token: string) => {
    try {
      setAcceptingInvite(token);
      await api.post(`/family-groups/invite/${token}`);
      
      // Refresh user data to update familyGroups array
      await refreshUser();
      
      // Refresh both groups and invitations
      await fetchGroups();
      await fetchInvitations();
      setAcceptingInvite(null);
    } catch (error) {
      logger.error('Error accepting invitation:', error);
      setAcceptingInvite(null);
    }
  };

  const handleRemoveMember = async (groupId: string, userId: string) => {
    try {
      await api.delete(`/family-groups/${groupId}/members/${userId}`);
      fetchGroups();
    } catch (error) {
      logger.error('Error removing member:', error);
    }
  };

  const handleRoleChange = async (groupId: string, userId: string, newRole: 'editor' | 'viewer') => {
    try {
      await api.patch(`/family-groups/${groupId}/members/${userId}`, { role: newRole });
      fetchGroups();
    } catch (error) {
      logger.error('Error updating member role:', error);
      setError('Failed to update member role. Please try again.');
    }
  };

  const handleDeleteGroup = async () => {
    if (!groupToDelete) return;

    try {
      const response = await api.delete(`/family-groups/${groupToDelete._id}`);
      if (response.status === 200) {
        setFamilyGroups(familyGroups.filter(group => group._id !== groupToDelete._id));
        setIsDeleteModalOpen(false);
        setGroupToDelete(null);
      }
    } catch (error) {
      logger.error('Error deleting group:', error);
    }
  };

  const handleEditGroup = async () => {
    if (!groupToEdit) return;

    try {
      const response = await api.patch(`/family-groups/${groupToEdit._id}`, {
        name: editedGroup.name,
        description: editedGroup.description,
      });
      
      if (response.status === 200) {
        setFamilyGroups(familyGroups.map(group => 
          group._id === groupToEdit._id ? { ...group, ...editedGroup } : group
        ));
        setIsEditModalOpen(false);
        setGroupToEdit(null);
        setEditedGroup({ name: '', description: '' });
      }
    } catch (error) {
      logger.error('Error updating group:', error);
    }
  };

  const isAdmin = (group: FamilyGroup) => {
    return group.members.some(member => 
      member.user._id === user?.id && member.role === 'admin'
    );
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="py-6">
      <div className="w-full px-4 sm:px-6">
        <Card>
          <div className="px-4 sm:px-6 py-5 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <h1 className="text-2xl font-semibold text-gray-900">Family Groups</h1>
              <Button 
                onClick={() => setIsCreateModalOpen(true)}
                className="w-full sm:w-auto justify-center"
              >
                Create Group
              </Button>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            {error && (
              <ErrorMessage
                message={error}
                className="mb-4"
                onDismiss={() => setError(null)}
              />
            )}

            {pendingInvitations.length > 0 && (
              <div className="mb-6">
                <h2 className="text-xl font-semibold mb-4">Pending Invitations</h2>
                <div className="space-y-4">
                  {pendingInvitations.map(invitation => (
                    <Card key={`invite-${invitation._id}`}>
                      <div className="p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                          <div>
                            <h3 className="font-medium">{invitation.name}</h3>
                            <p className="text-sm text-gray-600 mt-1">{invitation.description}</p>
                            <p className="text-sm text-gray-500 mt-2">
                              Invited by: {invitation.admin.firstName} {invitation.admin.lastName}
                            </p>
                            <p className="text-sm text-gray-500">
                              Role: {invitation.invitation.role === 'editor' ? 'Editor' : 'Viewer'}
                            </p>
                          </div>
                          <Button
                            variant="primary"
                            size="small"
                            onClick={() => handleAcceptInvitation(invitation.invitation.token)}
                            disabled={acceptingInvite === invitation.invitation.token}
                            className="w-full sm:w-auto justify-center"
                          >
                            {acceptingInvite === invitation.invitation.token ? 'Accepting...' : 'Accept Invitation'}
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {familyGroups.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">You don't have any family groups yet.</p>
                <Button 
                  variant="primary" 
                  className="mt-4 w-full sm:w-auto justify-center"
                  onClick={() => setIsCreateModalOpen(true)}
                >
                  Create Your First Group
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {familyGroups.map(group => (
                  <Card key={group._id} className="overflow-hidden">
                    <div className="p-4 sm:p-6">
                      <div className="space-y-4">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-4 sm:space-y-0">
                          <div>
                            <h2 className="text-xl font-semibold">{group.name}</h2>
                            <p className="text-gray-600 mt-1">{group.description}</p>
                            <p className="text-sm text-gray-500 mt-2">
                              Admin: {group.admin.firstName} {group.admin.lastName}
                            </p>
                            <p className="text-sm text-gray-500">
                              {group.members.length} {group.members.length === 1 ? 'member' : 'members'}
                            </p>
                          </div>
                          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => {
                                setGroupToEdit(group);
                                setEditedGroup({
                                  name: group.name,
                                  description: group.description
                                });
                                setIsEditModalOpen(true);
                              }}
                              className="w-full sm:w-auto justify-center"
                            >
                              Edit Group
                            </Button>
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => {
                                setSelectedGroupId(group._id);
                                setIsInviteModalOpen(true);
                              }}
                              className="w-full sm:w-auto justify-center"
                            >
                              Manage Settings
                            </Button>
                            {isAdmin(group) && (
                              <Button
                                variant="danger"
                                size="small"
                                onClick={() => {
                                  setGroupToDelete(group);
                                  setIsDeleteModalOpen(true);
                                }}
                                className="w-full sm:w-auto justify-center"
                              >
                                Delete Group
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </Card>

        <Modal
          isOpen={isCreateModalOpen}
          onClose={() => {
            setIsCreateModalOpen(false);
          }}
          title="Create Family Group"
          actions={{
            primary: {
              label: 'Create Group',
              onClick: handleCreateGroup,
              isLoading: isCreating,
              disabled: isCreating
            },
            secondary: {
              label: 'Cancel',
              onClick: () => {
                setIsCreateModalOpen(false);
              },
              disabled: isCreating
            }
          }}
          afterLeave={() => {
            setNewGroup({ name: '', description: '' });
            setCreateError(null);
            setIsCreating(false);
          }}
        >
          <div className="space-y-4">
            {createError && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="text-sm text-red-700">{createError}</div>
              </div>
            )}
            <TextInput
              label="Name"
              value={newGroup.name}
              onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
              placeholder="Enter group name"
              required
            />
            <TextInput
              label="Description"
              value={newGroup.description}
              onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
              placeholder="Enter group description"
            />
          </div>
        </Modal>

        <Modal
          isOpen={isInviteModalOpen}
          onClose={() => {
            setIsInviteModalOpen(false);
          }}
          title="Manage Group Settings"
          size="xl"
          actions={{
            primary: {
              label: 'Done',
              onClick: () => setIsInviteModalOpen(false)
            }
          }}
          afterLeave={() => {
            setNewInvite({ email: '', role: 'viewer' });
            setError(null);
            setSelectedGroupId(null);
          }}
        >
          {selectedGroupId && (
            <div className="space-y-6">
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 mb-4">
                  <h3 className="text-lg font-medium">Members</h3>
                  {isAdmin(familyGroups.find(g => g._id === selectedGroupId)!) && (
                    <div className="w-full sm:w-auto">
                      <TextInput
                        label="Invite Member"
                        value={newInvite.email}
                        onChange={(e) => setNewInvite({ ...newInvite, email: e.target.value })}
                        placeholder="Enter email address"
                      />
                      <div className="flex flex-col sm:flex-row gap-2 mt-2">
                        <select
                          className="block w-full sm:w-auto rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          value={newInvite.role}
                          onChange={(e) => setNewInvite({ ...newInvite, role: e.target.value as 'editor' | 'viewer' })}
                        >
                          <option value="viewer">Viewer</option>
                          <option value="editor">Editor</option>
                        </select>
                        <Button
                          variant="primary"
                          onClick={handleInviteMember}
                          className="w-full sm:w-auto justify-center"
                        >
                          Invite Member
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="bg-gray-50 rounded-lg overflow-hidden">
                  <ul className="divide-y divide-gray-200">
                    {familyGroups.find(g => g._id === selectedGroupId)?.members.map(member => (
                      <li key={member.user._id} className="p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                          <div>
                            <p className="font-medium text-gray-900">
                              {member.user.firstName} {member.user.lastName}
                            </p>
                            <div className="mt-2">
                              {isAdmin(familyGroups.find(g => g._id === selectedGroupId)!) && member.role !== 'admin' ? (
                                <select
                                  className="block w-full sm:w-auto rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                  value={member.role}
                                  onChange={(e) => handleRoleChange(selectedGroupId, member.user._id, e.target.value as 'editor' | 'viewer')}
                                >
                                  <option value="viewer">Viewer</option>
                                  <option value="editor">Editor</option>
                                </select>
                              ) : (
                                <p className="text-sm text-gray-500">
                                  Role: {member.role}
                                </p>
                              )}
                            </div>
                          </div>
                          {isAdmin(familyGroups.find(g => g._id === selectedGroupId)!) && 
                           member.user._id !== user?.id && 
                           member.role !== 'admin' && (
                            <Button
                              variant="danger"
                              size="small"
                              onClick={() => handleRemoveMember(selectedGroupId, member.user._id)}
                              className="w-full sm:w-auto justify-center"
                            >
                              Remove Member
                            </Button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              {isAdmin(familyGroups.find(g => g._id === selectedGroupId)!) && (
                <div className="pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-medium mb-4">Categories</h3>
                  <div className="categories-container">
                    <CategoryManager
                      familyGroupId={selectedGroupId}
                      onCategoryChange={fetchGroups}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal>

        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
          }}
          title="Delete Family Group"
          actions={{
            primary: {
              label: 'Delete Group',
              onClick: handleDeleteGroup
            },
            secondary: {
              label: 'Cancel',
              onClick: () => {
                setIsDeleteModalOpen(false);
              }
            }
          }}
          afterLeave={() => {
            setGroupToDelete(null);
          }}
        >
          <div className="space-y-4">
            <p className="text-gray-700">
              Are you sure you want to delete the family group "{groupToDelete?.name}"? This action cannot be undone.
            </p>
            <p className="text-sm text-gray-500">
              All items, categories, and member associations will be permanently deleted.
            </p>
          </div>
        </Modal>

        <Modal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
          }}
          title="Edit Family Group"
          actions={{
            primary: {
              label: 'Save Changes',
              onClick: handleEditGroup
            },
            secondary: {
              label: 'Cancel',
              onClick: () => {
                setIsEditModalOpen(false);
              }
            }
          }}
          afterLeave={() => {
            setGroupToEdit(null);
            setEditedGroup({ name: '', description: '' });
          }}
        >
          <div className="space-y-4">
            <TextInput
              label="Name"
              value={editedGroup.name}
              onChange={(e) => setEditedGroup(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter group name"
              required
            />
            <TextInput
              label="Description"
              value={editedGroup.description}
              onChange={(e) => setEditedGroup(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter group description"
            />
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default Groups; 